import {BIOMARKER_STATUS_COLORS, getStatusColor} from '@/shared/constants/colors';
import type {BiomarkerStatus, BiomarkerZone, CustomBiomarkerConfig} from '@/shared/types/biomarker';

export interface ProgressBarSection {
    width: number;
    color: string;
}
export interface OptimalZoneInfo {
    label: string;
    startPercent: number;
    endPercent: number;
    minValue: number;
    maxValue: number;
}
export interface ResolvedColors {
    low: string;
    normal: string;
    high: string;
    normalLabel: string;
}

export type SeverityTier = 'critical' | 'borderline' | 'normal' | 'unknown';

/** Canonical status→tier, aligned to the color buckets in colors.ts so a
 *  status's chip color, chart-zone color, and count bucket always agree. */
export function severityTier(status: BiomarkerStatus): SeverityTier {
    switch (status) {
        case 'Normal':
        case 'Optimal':
        case 'Negative':
            return 'normal';
        case 'Low':
        case 'Borderline':
        case 'Elevated':
        case 'Deficient':
        case 'Impaired':
            return 'borderline';
        case 'High':
        case 'Toxic':
        case 'Positive':
            return 'critical';
        default:
            return 'unknown';
    }
}

export interface ClassifyParams {
    rangeMin: number;
    rangeMax: number;
    rawRefRange: string;
    customConfig: CustomBiomarkerConfig | null | undefined;
    isBinary: boolean;
    value: number | undefined;
    binaryStatus: 'Positive' | 'Negative' | null;
    /** Backend abnormal flag; used only for the non-numeric, no-range tail. */
    abnormal?: string | null;
}

export interface BiomarkerClassification {
    status: BiomarkerStatus;
    /** The band label when a display rule matched, else the status string —
     *  this is what the UI should render. */
    displayLabel: string;
    tier: SeverityTier;
    color: string;
    resolvedColors: ResolvedColors;
    progressBarSections: ProgressBarSection[];
    optimalZoneInfo: OptimalZoneInfo;
    indicatorPercent: number;
    hasValue: boolean;
    hasRefRange: boolean;
    refRangeHasBothBounds: boolean;
}

function resolveColors(customConfig: CustomBiomarkerConfig | null | undefined): ResolvedColors {
    if (!customConfig || customConfig.zones.length === 0) {
        return {
            low: BIOMARKER_STATUS_COLORS.LOW,
            normal: BIOMARKER_STATUS_COLORS.NORMAL,
            high: BIOMARKER_STATUS_COLORS.HIGH,
            normalLabel: 'Optimal',
        };
    }
    const normalZone = customConfig.zones.find((z) => z.status === 'Normal' || z.status === 'Optimal');
    const lowZone = customConfig.zones.find((z) => z.status === 'Low' || z.status === 'Deficient');
    const highZone = customConfig.zones.find((z) => z.status === 'High' || z.status === 'Elevated');
    return {
        low: lowZone?.color ?? BIOMARKER_STATUS_COLORS.LOW,
        normal: normalZone?.color ?? BIOMARKER_STATUS_COLORS.NORMAL,
        high: highZone?.color ?? BIOMARKER_STATUS_COLORS.HIGH,
        normalLabel: normalZone?.label ?? 'Optimal',
    };
}

/** Config-zone membership → status + indicator position, mirroring the
 *  useBiomarkerZones custom-zone math (proportional within-zone, 2x window
 *  above the last zone). */
function classifyByConfigZones(
    zones: CustomBiomarkerConfig['zones'],
    value: number | undefined
): {status: BiomarkerStatus; indicatorPercent: number; zone: BiomarkerZone | null} {
    if (value == null || !Number.isFinite(value)) {
        return {status: 'Unknown', indicatorPercent: 50, zone: null};
    }
    let cumulative = 0;
    for (let i = 0; i < zones.length; i++) {
        const zone = zones[i];
        const isLast = i === zones.length - 1;
        const inZone = isLast ? value >= zone.min && value <= zone.max : value >= zone.min && value < zone.max;
        if (inZone) {
            const positionInZone = (value - zone.min) / (zone.max - zone.min);
            return {status: zone.status, indicatorPercent: cumulative + positionInZone * zone.percentage, zone};
        }
        cumulative += zone.percentage;
    }
    // Below the first zone
    const firstZone = zones[0];
    if (value < firstZone.min) {
        const zoneRange = firstZone.max - firstZone.min;
        const distance = firstZone.min - value;
        const proportional = Math.max(0, (zoneRange - distance) / zoneRange);
        return {status: firstZone.status, indicatorPercent: proportional * firstZone.percentage, zone: firstZone};
    }
    // Above the last zone
    const lastZone = zones[zones.length - 1];
    const totalBeforeLast = zones.slice(0, -1).reduce((sum, z) => sum + z.percentage, 0);
    const zoneRange = lastZone.max - lastZone.min;
    const distance = value - lastZone.max;
    const proportional = Math.min(1, (zoneRange + distance) / (zoneRange * 2));
    return {
        status: lastZone.status,
        indicatorPercent: totalBeforeLast + proportional * lastZone.percentage,
        zone: lastZone,
    };
}

/** Standard 3-zone indicator, mirrors useBiomarkerZones. */
function threeZoneIndicator(value: number, rangeMin: number, rangeMax: number): number {
    const rangeWidth = rangeMax - rangeMin;
    if (rangeWidth <= 0) return 50;
    if (value >= rangeMin && value <= rangeMax) {
        const positionInRange = (value - rangeMin) / rangeWidth;
        return 33.33 + positionInRange * 33.34;
    }
    if (value < rangeMin) {
        const lowZoneMin = rangeMin - rangeWidth;
        const clamped = Math.max(value, lowZoneMin);
        return ((clamped - lowZoneMin) / rangeWidth) * 33.33;
    }
    const highZoneMax = rangeMax + rangeWidth;
    const clamped = Math.min(value, highZoneMax);
    return 66.67 + ((clamped - rangeMax) / rangeWidth) * 33.33;
}

export function classifyBiomarker(p: ClassifyParams): BiomarkerClassification {
    const {rangeMin, rangeMax, rawRefRange, customConfig, isBinary, value, binaryStatus, abnormal} = p;

    const hasRefRange = rangeMin !== 0 || rangeMax !== 100;
    const cleanedRefRange = rawRefRange.replace(/[≤≥]/g, '').trim();
    const refRangeHasBothBounds = hasRefRange && /\d+\.?\d*\s*-\s*\d+\.?\d*/.test(cleanedRefRange);
    const resolvedColors = resolveColors(customConfig);
    const hasValue = isBinary || (value != null && Number.isFinite(value));

    const finish = (
        status: BiomarkerStatus,
        progressBarSections: ProgressBarSection[],
        optimalZoneInfo: OptimalZoneInfo,
        indicatorPercent: number,
        override?: {label: string; severity: SeverityTier; color: string}
    ): BiomarkerClassification => ({
        status,
        displayLabel: override?.label ?? status,
        tier: override?.severity ?? severityTier(status),
        // The zone's own colour, not a severity lookup: two bands can share a
        // severity and still render differently (HDL Healthy vs Optimal).
        color: override?.color ?? getStatusColor(status),
        resolvedColors,
        progressBarSections,
        optimalZoneInfo,
        indicatorPercent,
        hasValue,
        hasRefRange,
        refRangeHasBothBounds,
    });

    // 1. Binary
    if (isBinary) {
        const status: BiomarkerStatus = binaryStatus === 'Positive' ? 'Positive' : 'Negative';
        return finish(
            status,
            [
                {width: 50, color: BIOMARKER_STATUS_COLORS.NORMAL},
                {width: 50, color: BIOMARKER_STATUS_COLORS.HIGH},
            ],
            {label: 'Negative', startPercent: 0, endPercent: 50, minValue: rangeMin, maxValue: rangeMax},
            binaryStatus === 'Positive' ? 75 : 25
        );
    }

    // 2. Config-first (Option B): a config with zones wins even if a lab range exists.
    if (customConfig && customConfig.zones.length > 0) {
        const zones = customConfig.zones;
        const {status, indicatorPercent, zone: matchedZone} = classifyByConfigZones(zones, value);
        const progressBarSections = zones.map((z) => ({width: z.percentage, color: z.color}));
        const optimalZone = zones.find((z) => z.status === 'Normal' || z.status === 'Optimal');
        let optimalZoneInfo: OptimalZoneInfo;
        if (optimalZone) {
            let cumulative = 0;
            for (const z of zones) {
                if (z === optimalZone) break;
                cumulative += z.percentage;
            }
            optimalZoneInfo = {
                label: optimalZone.label,
                startPercent: cumulative,
                endPercent: cumulative + optimalZone.percentage,
                minValue: hasRefRange ? rangeMin : optimalZone.min,
                maxValue: hasRefRange ? rangeMax : optimalZone.max,
            };
        } else {
            optimalZoneInfo = {
                label: resolvedColors.normalLabel,
                startPercent: 33.33,
                endPercent: 66.67,
                minValue: rangeMin,
                maxValue: rangeMax,
            };
        }
        const override = matchedZone?.severity
            ? {label: matchedZone.label, severity: matchedZone.severity as SeverityTier, color: matchedZone.color}
            : undefined;
        return finish(status, progressBarSections, optimalZoneInfo, indicatorPercent, override);
    }

    // 3. Lab ref-range 3-zone (no config).
    if (hasRefRange) {
        const progressBarSections = [
            {width: 33.33, color: resolvedColors.low},
            {width: 33.33, color: resolvedColors.normal},
            {width: 33.33, color: resolvedColors.high},
        ];
        const optimalZoneInfo: OptimalZoneInfo = {
            label: resolvedColors.normalLabel,
            startPercent: 33.33,
            endPercent: 66.67,
            minValue: rangeMin,
            maxValue: rangeMax,
        };
        if (value == null || !Number.isFinite(value)) {
            return finish(abnormalStatus(abnormal) ?? 'Unknown', progressBarSections, optimalZoneInfo, 50);
        }
        const status: BiomarkerStatus = value < rangeMin ? 'Low' : value > rangeMax ? 'High' : 'Normal';
        return finish(status, progressBarSections, optimalZoneInfo, threeZoneIndicator(value, rangeMin, rangeMax));
    }

    // 4. Default (no config, no usable range).
    const progressBarSections = [
        {width: 33.33, color: BIOMARKER_STATUS_COLORS.LOW},
        {width: 33.33, color: BIOMARKER_STATUS_COLORS.NORMAL},
        {width: 33.33, color: BIOMARKER_STATUS_COLORS.HIGH},
    ];
    const optimalZoneInfo: OptimalZoneInfo = {
        label: 'Optimal',
        startPercent: 33.33,
        endPercent: 66.67,
        minValue: rangeMin,
        maxValue: rangeMax,
    };
    if (value == null || !Number.isFinite(value)) {
        return finish(abnormalStatus(abnormal) ?? 'Unknown', progressBarSections, optimalZoneInfo, 50);
    }
    const status: BiomarkerStatus = value < 1 ? 'Low' : value > 100 ? 'High' : 'Normal';
    return finish(status, progressBarSections, optimalZoneInfo, threeZoneIndicator(value, 1, 100));
}

/** Map a backend abnormal flag to a status, or null if unrecognized. */
function abnormalStatus(abnormal: string | null | undefined): BiomarkerStatus | null {
    if (!abnormal) return null;
    const a = abnormal.toLowerCase();
    if (a.includes('high') || a === 'h') return 'High';
    if (a.includes('low') || a === 'l') return 'Low';
    if (a === 'normal') return 'Normal';
    return null;
}
